// ============================================================================
// PATIENT WEB — legacy client, now WIRED to the real patient-backend
// (/api/patient on port 4000, one MongoDB shared with the APK preview).
// ----------------------------------------------------------------------------
// Method names/signatures are UNCHANGED so no page needs edits. Bodies call the
// real backend and ADAPT responses to the legacy types in ./types. Endpoints
// with no backend equivalent yet keep their old paths — they 404 and pages use
// their existing demo fallback (unchanged behaviour, honestly documented in
// docs/PATIENT_WEB_APK_FULL_INTERNAL_LINKING_REPORT.md). NO /api/ecc anywhere.
// ============================================================================
import { patientApi as http, setPatientToken } from '../../api/http';
import { saveToken, savePatientSummary, clearToken } from '../../api/patientSession';
import type {
  DashboardSummary, PatientProfile, EmergencyProfile, TimelineEvent,
  MedicineReminder, InsurancePolicy, InsuranceClaim, FamilyMember,
  LabSample, LabReport, LabTracking, AccessLogEntry, PharmacyOrder, FollowUp,
  MarketplaceProvider, MarketplaceStats,
  PatientCertificateDoc, HealthVaultItem, DocumentShare, DocumentAuditLog,
  EcosystemLink, NeoVerifyDoc,
  CommSummary, PatientConversation, PatientChatMessage, PatientCallLogEntry,
  PatientTelemedicineSession,
} from './types';
import type { ConsentGrant } from './state/patientProductionStore';

// ─── backend-shaped records with no legacy type (new real endpoints) ─────────
export interface PrescriptionRecord {
  id: string;
  appointmentId: string;
  doctorName: string;
  clinicName: string | null;
  appointmentDate: string | null;
  chiefComplaint: string;
  diagnosis: string;
  medicines: Record<string, unknown>[];
  notes: string | null;
  status: string;
  signedAt: string | null;
  createdAt: string;
}

/**
 * A notification exactly as the backend stores it. `category` is the server's
 * own enum (notification.model.ts), which is what the bell badge and the
 * Notifications page both filter on — the page used to render a local-only
 * list while the badge counted these, so the two never agreed.
 */
export type PatientNotificationCategory =
  | 'APPOINTMENT' | 'LAB' | 'MEDICINE' | 'RECORDS' | 'EMERGENCY'
  | 'CONSENT' | 'ORDER' | 'INSURANCE' | 'SECURITY' | 'GENERAL';

export interface PatientNotificationRow {
  id: string;
  title: string;
  body: string | null;
  category: PatientNotificationCategory | string;
  read: boolean;
  createdAt: string;
}

/** An imaging order as the backend returns it (patientImagingCompat). */
export interface ImagingOrderRecord {
  id: string;
  orderNumber: string | null;
  modality: string | null;
  testName: string | null;
  bodyPart: string | null;
  facility: string | null;
  orderedBy: string | null;
  orderedAt: string | null;
  scheduledFor: string | null;
  status: string;
  sourceStatus?: string | null;
  source: string | null;
  priority: string | null;
  critical: boolean;
  reportAvailable: boolean;
}

/** One row of the unified payments list. */
export interface PatientPaymentRow {
  module: string;
  reference: string;
  title: string;
  providerName: string | null;
  providerNh12?: string | null;
  amount: number;
  paid: boolean;
  status: string;
  invoiceNumber: string | null;
  paidAt: string | null;
  createdAt: string;
}

export interface PatientPaymentsList {
  payments: PatientPaymentRow[];
  /** What the patient still owes across every module, as the server totals it. */
  dueTotal: number;
  /** Bills this app cannot take money for, in the server's own words. */
  notPayableInApp: string[];
}

export interface PatientPaymentDetail extends PatientPaymentRow {
  providerPhone?: string | null;
  lines: { label: string; amount: number }[];
  paymentMethod?: string | null;
  receiptNumber?: string | null;
  methods?: { id: string; label: string; note?: string; online?: boolean }[];
  upi?: { id?: string; name?: string } | null;
  /** A transfer reference already sent, waiting on the provider. */
  pendingProof?: { method?: string; utr?: string; status?: string; submittedAt?: string } | null;
  insurance?: { hasActivePolicy: boolean; policies?: { providerName: string; policyNumber: string }[]; note?: string } | null;
  gateway?: { available: boolean; note?: string } | null;
}

/** One published opening from `GET /providers/:idOrSlug/slots`. */
export interface ProviderSlot {
  id: string;
  /** YYYY-MM-DD in the clinic's own calendar. */
  date: string;
  startTime: string;
  endTime: string;
  mode: string;
  /** Seats left; a slot with none is not returned at all. */
  remaining: number;
  clinicId: string;
  clinicName: string | null;
}

export interface ProviderSlotsResponse {
  providerId: string;
  providerName: string;
  /** False when this provider publishes no schedule at all. */
  hasSchedule: boolean;
  slots: ProviderSlot[];
}

/** A lab home-collection visit as the backend reports it. */
export interface LabHomeCollection {
  id: string;
  orderNumber: string | null;
  address: string | null;
  pincode: string | null;
  scheduledAt: string | null;
  phlebotomistName: string | null;
  phlebotomistNhcId: string | null;
  status: string;
  patientVerified: boolean;
  failReason: string | null;
  timeline: { status: string; note: string | null; at: string }[];
}

/** The second factor as the server reports it (`GET /auth/2fa`). */
export interface TwoFactorStatus {
  /** The one-time-code step at login. */
  otpStepUpEnabled: boolean;
  phone: string | null;
  email: string | null;
  totp: { setUp: boolean; confirmed: boolean; confirmedAt: string | null };
  recoveryCodes: { total: number; remaining: number; generatedAt: string | null };
  trustedDevices: { id: string; label: string; addedAt?: string | null; lastSeenAt?: string | null }[];
}

/** A request from a provider, waiting on the patient's answer. */
export interface ConsentRequestRow {
  id: string;
  requester: string;
  requesterType: string | null;
  requesterNhcId: string | null;
  purpose: string | null;
  records: string[];
  duration: string | null;
  status: string;
  requestedAt: string;
  respondedAt: string | null;
}

/** A standing connection — someone who can see records right now. */
export interface ConsentConnectionRow {
  id: string;
  label: string | null;
  name: string;
  nhcId: string | null;
  role: string | null;
  accent: string | null;
  status: string;
  scopes: string[];
  validUntil: string | null;
  revocable: boolean;
  detail: string | null;
}

export interface ConsentOverview {
  connections: ConsentConnectionRow[];
  pending: ConsentRequestRow[];
  approved: ConsentRequestRow[];
  declined: ConsentRequestRow[];
  past: ConsentConnectionRow[];
}

export interface HospitalSummary {
  activeEncounters: number;
  liveTokens: number;
  unpaidBills: number;
  /** False when no hospital has this patient's card on file. */
  linked: boolean;
}

/**
 * A hospital's own published panel. Every field here is something the hospital
 * switched on in its own app: a licence number it has not had verified comes
 * back null, and a price list it chose not to publish simply is not sent.
 */
export interface HospitalPanel {
  id: string;
  isLive: boolean;
  displayName: string;
  hospitalNhcId: string | null;
  verificationStatus?: string | null;
  /** Granted by NeoHealthCard, never self-asserted. */
  verified?: boolean;
  hospitalType?: string | null;
  ownershipType?: string | null;
  addressLine?: string | null;
  city?: string | null;
  state?: string | null;
  pincode?: string | null;
  phone?: string | null;
  website?: string | null;
  logoUrl?: string | null;
  registrationNumber?: string | null;
  accreditation?: string | null;
  licences?: { licenceId: string; title: string; number: string | null; status: string; nhcVerified: boolean; validTo: string | null }[];
  emergency?: { available: boolean; contact: string | null } | null;
  facilities?: { key: string; label: string }[];
  departments?: { id: string; name: string }[];
  services?: { id: string; name: string; basePrice?: number }[];
  doctors?: { id: string; name: string; specialty: string | null }[];
  showPrices?: boolean;
  publicBookingEnabled?: boolean;
  publicEnquiryChatEnabled?: boolean;
}

export interface HospitalDirectory {
  items: HospitalPanel[];
  page: number;
  limit: number;
  total: number;
}

export interface HospitalBookingInput {
  scheduledAt: string;
  departmentId?: string;
  doctorId?: string;
  serviceId?: string;
  reasonForVisit?: string;
}

export interface HospitalBookingResult {
  appointmentId: string;
  appointmentNumber: string;
  hospitalName: string;
  hospitalPhone: string | null;
  departmentName: string | null;
  doctorName: string | null;
  scheduledAt: string;
  status: string;
  tokenNumber: string | number | null;
  serviceFee: number;
  paymentStatus: string;
  /** The hospital has not confirmed it yet; it is queued as a walk-in would be. */
  awaitingHospitalConfirmation: boolean;
}

export interface HospitalEncounter {
  encounterId: string;
  kind: string;
  hospitalName: string;
  hospitalNhcId: string | null;
  departmentName: string | null;
  attendingDoctorName: string | null;
  status: string;
  active: boolean;
  tokenNumber: string | number | null;
  ward: string | null;
  roomNumber: string | null;
  diagnosis: string | null;
  reasonForVisit: string | null;
  scheduledAt: string | null;
  admittedAt: string | null;
  dischargedAt: string | null;
  lastEventAt: string | null;
  timeline: { status: string; at: string }[];
}

export interface HospitalBill {
  billId: string;
  billNumber: string;
  invoiceNumber: string | null;
  hospitalName: string;
  grossAmount: number;
  taxAmount: number;
  netAmount: number;
  paidAmount: number;
  /** What is still on the patient after insurance and payments. */
  patientPayable: number;
  paymentStatus: string;
  insuranceStatus: string | null;
  claimStatus: string | null;
  lineItems: { description: string; quantity: number; unitPrice: number; amount: number; category?: string }[];
  createdAt: string;
}

/** One end of an ambulance trip, as the booking records it. */
export interface AmbulancePlace {
  address: string | null;
  lat: number | null;
  lng: number | null;
  landmark: string | null;
  contactName: string | null;
  contactPhone: string | null;
  pincode: string | null;
}

export interface AmbulanceTrip {
  id: string;
  bookingNumber: string;
  requestType: string;
  ambulanceType: string;
  patientName: string | null;
  patientPhone: string | null;
  nhcId: string | null;
  pickup: AmbulancePlace | null;
  destination: AmbulancePlace | null;
  status: string;
  /**
   * What the patient told the operator. `severity` is their own grade, or null
   * when they did not give one — read back so a screen can show what was
   * actually said rather than what this app assumed it sent.
   */
  triage?: { conditions: string[]; painScore: number | null; severity: string | null; notes: string | null };
  /** True while the patient may still call it off. */
  cancellable: boolean;
  fare: { total?: number; currency?: string } | null;
  assignment: { vehicleNumber?: string; crewName?: string; crewPhone?: string; etaMinutes?: number } | null;
  timeline: { status: string; at: string; note?: string | null }[];
  createdAt: string;
  updatedAt?: string;
}

export interface AmbulanceEmergencyProfile {
  patientName: string;
  nhcId: string | null;
  bloodGroup: string | null;
  allergies: string[];
  contacts: { name: string; relation: string | null; phone: string; source: string }[];
  /** Exactly which fields a crew is allowed to read. */
  emergencyScopes: string[];
}

export interface AmbulanceDestination {
  providerId: string;
  name: string;
  address: string | null;
  city: string | null;
  phone: string | null;
  verified: boolean;
  specialties: string[];
  distanceKm: number | null;
  erBedsFree: number | null;
}

export interface PharmacyOrderFull {
  id: string;
  /**
   * The patient-app marketplace order this came from, when it came from one.
   * Both `/medicine-orders` and `/pharmacy/orders` now return such an order —
   * a bridged order is in both — so this is what lets a screen showing both
   * lists tell one order from two.
   */
  patientOrderId?: string | null;
  orderNumber: string;
  status: string;
  pharmacy: { name?: string | null; nh12?: string | null; phone?: string | null } | null;
  items: { medicineName?: string; name?: string; quantity?: number; unitPrice?: number; amount?: number }[];
  subtotal: number;
  taxAmount: number;
  deliveryFee: number;
  totalAmount: number;
  paymentStatus: string;
  paymentMethod: string | null;
  paidAt: string | null;
  fulfillmentType: string | null;
  prescriptionId: string | null;
  prescriptionRequired: boolean;
  delivery: { address?: string | null; status?: string | null; etaMinutes?: number | null } | null;
  invoice: { invoiceNumber?: string | null; url?: string | null } | null;
  timeline: { status: string; at: string; note?: string | null }[];
  cancellationReason: string | null;
  placedAt: string;
}

/** A substitute the pharmacy proposed, waiting on the patient. */
/**
 * What one prescription would cost at each pharmacy that stocks it.
 *
 * `estimateTotal` is one unit of each matched medicine — quantities are
 * decided in the cart — and only a store with nothing `missing` can quote the
 * whole prescription, which is what `complete` says.
 */
/** One test, priced by every lab that publishes it (`GET /lab/tests/compare`). */
export interface ProviderReview {
  id: string;
  /** First name and an initial — the server shortens it, not the app. */
  author: string;
  rating: number;
  title: string | null;
  comment: string | null;
  /** The server's own finding that this reviewer had a visit on record. */
  verifiedVisit: boolean;
  visitKind: string | null;
  at: string;
}

export interface ProviderReviews {
  providerId: string;
  providerName: string;
  rating: number;
  reviewCount: number;
  /** False when the star average has no written reviews behind it. */
  ratingIsFromReviews: boolean;
  breakdown: { star: number; count: number }[];
  reviews: ProviderReview[];
}

export interface MyProviderReview {
  id: string;
  rating: number;
  title: string | null;
  comment: string | null;
  verifiedVisit: boolean;
  visitKind: string | null;
  at: string;
}

/** One recorded touch of the patient's record. */
export interface AccessTrailEntry {
  id: string;
  actor: string;
  actorNhcId: string | null;
  role: string;
  action: string;
  detail: string | null;
  at: string;
  section: string;
  basis: string;
  /** True when the patient did it themselves. */
  byYou: boolean;
  accent: string;
}

export interface AccessTrail {
  provider: AccessTrailEntry[];
  emergency: AccessTrailEntry[];
  imaging: AccessTrailEntry[];
  log: AccessTrailEntry[];
  total: number;
  /**
   * Real counts from the database, over the real period — not a tally of
   * whatever the page happened to hold. Asking for a bigger page used to make
   * "this week" go up, which made the headline number meaningless.
   */
  weekCount: number;
  /** Of those, the ones done by somebody other than the patient. */
  othersWeekCount: number;
  /** Every access by somebody else, ever — so a section is never a sample. */
  othersTotal: number;
  /** True when the raw `log` feed hit its page limit. Nothing more. */
  capped: boolean;
}

export interface SecurityEvent {
  id: string;
  action: string;
  type: string;
  title: string;
  device: string | null;
  ip: string | null;
  detail: string | null;
  at: string;
  ok: boolean;
}

export interface SecurityEvents {
  events: SecurityEvent[];
  total: number;
  types: string[];
}

/** A lab test request a doctor raised and sent (`GET /lab/orders`). */
export interface DoctorLabOrder {
  id: string;
  appointmentId: string | null;
  doctorName: string | null;
  orderedOn: string | null;
  appointmentDate: string | null;
  priority: string | null;
  clinicalNotes: string | null;
  tests: { name: string; category: string | null; sampleType: string | null; fasting: boolean; reason: string | null }[];
}

/** Which lab a catalogue row belongs to — every price says whose it is. */
export interface LabCatalogueLab {
  id: string;
  name: string;
  slug: string;
  verified: boolean;
  city: string | null;
  homeCollection: boolean;
  rating: number | null;
}

/** One bookable test, as some lab publishes it (`GET /lab/tests`). */
export interface LabCatalogueTest {
  id: string;
  code: string | null;
  name: string;
  category: string | null;
  price: number;
  sampleType: string | null;
  containerType: string | null;
  turnaroundHours: number | null;
  note: string | null;
  parameters: { name: string; unit: string | null }[];
  lab: LabCatalogueLab;
}

/** One health package, as some lab publishes it (`GET /lab/packages`). */
export interface LabCataloguePackage {
  id: string;
  code: string | null;
  name: string;
  price: number;
  description: string | null;
  preparation: string | null;
  includes: string[];
  lab: LabCatalogueLab;
}

export interface LabTestComparison {
  name: string;
  rows: {
    labId: string;
    labName: string;
    labSlug: string;
    verified: boolean;
    city: string | null;
    price: number;
    turnaroundHours: number | null;
    sampleType: string | null;
    parameterCount: number;
    parameters: string[];
  }[];
}

export interface PrescriptionQuote {
  prescriptionId: string;
  doctorName?: string | null;
  medicines: { name: string; strength: string | null }[];
  stores: {
    slug: string;
    name: string;
    city: string | null;
    nh12: string | null;
    verified: boolean;
    matched: { name: string; matchedAs: string; price: number; inStock: boolean }[];
    missing: string[];
    estimateTotal: number;
    complete: boolean;
  }[];
}

export interface PharmacySubstitution {
  id: string;
  orderId: string | null;
  orderNumber: string | null;
  pharmacyName: string | null;
  pharmacyNh12: string | null;
  medicineName: string | null;
  substituteName: string | null;
  reason: string | null;
  status: string;
  proposedBy: string | null;
  at: string;
  decidedAt: string | null;
}


/** Something a provider flagged for the patient's attention. */
export interface HomeAlert {
  id: string;
  severity: string;
  title: string;
  message: string;
  source: string | null;
  flaggedBy: string | null;
  reference: string | null;
  at: string;
  /** Which patient screen explains it, as a bare route segment. */
  route: string | null;
}

export type SupportTopic = 'ACCOUNT_RECOVERY' | 'RECORDS' | 'BOOKING' | 'PAYMENT' | 'PRIVACY' | 'OTHER';

export interface SupportRequest {
  id: string;
  ticketNumber: string;
  topic: SupportTopic | string;
  subject: string;
  message: string;
  contact: string | null;
  status: string;
  replies: { from: string; authorName: string | null; message: string; at: string }[];
  createdAt: string;
  updatedAt: string;
}

/** One record in the vault, with what the registry knows about it. */
export interface VaultRecord {
  id: string;
  source: string;
  category: string;
  registry: string | null;
  title: string;
  type: string | null;
  provider: string | null;
  issuerNhcId: string | null;
  date: string | null;
  documentNumber: string | null;
  neoVerifyId: string | null;
  /** VERIFIED, INSURER_CONFIRMED, SELF_ENTERED or UNREGISTERED. */
  verification: string;
}

/** What has happened to ONE document, from issue to the last time it was opened. */
export interface VaultProvenance {
  neoVerifyId: string;
  documentType: string | null;
  documentNumber: string | null;
  documentTitle: string | null;
  issuerName: string | null;
  issuerNhcId: string | null;
  patientNhcId: string | null;
  status: string;
  hashAlgorithm: string | null;
  stages: { stage: string; at: string; detail: string | null; auditLogged: boolean }[];
}

export interface NeoVerifyResult {
  neoVerifyId: string;
  /** AUTHENTIC, UNREGISTERED, or the registry's own verdict. */
  result: string;
  documentType: string | null;
  documentNumber: string | null;
  documentTitle: string | null;
  issuerName: string | null;
  issuerNhcId: string | null;
  patientNhcId: string | null;
  issuedAt: string | null;
  registeredAt: string | null;
  hashAlgorithm: string | null;
  registry: string | null;
  note: string | null;
}

export interface PaymentReceipt {
  module: string;
  reference: string;
  title: string;
  providerName: string | null;
  providerNh12: string | null;
  patientNhcId: string | null;
  lines: { label: string; amount: number }[];
  amount: number;
  paidAt: string | null;
  paymentMethod: string | null;
  invoiceNumber: string | null;
  receiptNumber: string | null;
  /** Scannable payload a desk can check. NOT a NeoVerify registration. */
  verifyPayload: string;
}

/** A delivery or collection address the patient keeps on file. */
export interface SavedAddress {
  id: string;
  label: string;
  line: string;
  landmark?: string;
  city?: string;
  pincode?: string;
  contactName?: string;
  contactPhone?: string;
  isDefault?: boolean;
}

/**
 * The open preferences blob on the profile. Only the keys this app writes
 * are typed; anything another surface stores is carried through untouched.
 */
export interface PatientPreferences {
  addresses?: SavedAddress[];
  searchLocation?: string;
  language?: string;
  /** How the patient prefers to be contacted about care. */
  contactPreference?: 'CHAT' | 'CALL' | 'SMS' | 'EMAIL';
  notifications?: Record<string, boolean>;
  [key: string]: unknown;
}

export interface ImagingOrderDetail extends ImagingOrderRecord {
  clinicalNotes: string | null;
  contrastRequired: boolean;
  radiationExposure: string | null;
  pregnancyCaution: string | null;
  /** What a DOCTOR asked for, in their own words (modality/bodyPart/…). */
  studies: Record<string, unknown>[];
  safetyChecklist: { pregnancy?: string; metalImplants?: boolean } | null;
  schedule: { slotDate?: string | null; slotTime?: string | null } | null;
  capture: { technicianName?: string | null; imageCount?: number | null; qualityStatus?: string | null } | null;
  report: {
    reportNumber?: string | null;
    impression?: string | null;
    radiologistName?: string | null;
    criticalAlert?: boolean;
    reportedAt?: string | null;
  } | null;
  timeline: { status: string; at: string; note?: string | null }[];
}

export interface CarePlan {
  groupId: string;
  goalLabel: string;
  goalIcon: string | null;
  planKey: string | null;
  summary: string | null;
  endDate: string | null;
  createdAt: string;
}

export interface EcosystemOverview {
  patient: { name: string; nhcId: string };
  connectedCount: number;
  nodes: {
    key: string;
    label: string;
    name: string | null;
    nhcId: string | null;
    detail: string | null;
    count: number;
    connected: boolean;
    accent?: string | null;
  }[];
}

export interface EmergencyRequestRow {
  id: string;
  emergencyType: string;
  severity: string | null;
  status: string;
  dispatched: boolean;
  location: { lat?: number; lng?: number; address?: string } | null;
  notes: string | null;
  createdAt: string;
}

export interface AmbulanceFareEstimate {
  quotes: { ambulanceType?: string; fleetName?: string; startPrice?: number; perKm?: number }[];
  byType: Record<string, { low?: number; high?: number }>;
  fleetsTotal: number;
  fleetsQuoting: number;
  low: number | null;
  high: number | null;
  distanceKm: number | null;
}

export interface AmbulanceBookingInput {
  requestType: 'EMERGENCY' | 'SCHEDULED';
  ambulanceType: string;
  /**
   * `lat`/`lng` are the device's real position, and they belong HERE rather
   * than in a triage note: the fleet operator's own request view reads
   * `pickup.lat` / `pickup.lng` to put the pin on a map. Coordinates written
   * into free text reached a human who then had to retype them.
   */
  pickup: { address: string; lat?: number; lng?: number; landmark?: string; contactName?: string; contactPhone?: string; pincode?: string };
  destination: { address: string; facilityName?: string; hospitalProviderId?: string; choiceMode?: string };
  /**
   * `severity` is the patient's own word for how bad it is — a real field the
   * dispatch board shows and sorts on, not a sentence buried in `notes`.
   * Omitted when they did not grade it; never defaulted.
   */
  triage?: { conditions?: string[]; painScore?: number; severity?: string; notes?: string };
}

export interface PrescribedMedicine {
  id: string;
  medicineName: string;
  composition: string | null;
  dose: string | null;
  frequency: string | null;
  duration: string | null;
  instructions: string | null;
  doctorName: string | null;
  prescriptionId: string | null;
  prescribedAt: string | null;
  status: string;
}

export interface PatientSessionInfo {
  jti: string;
  deviceLabel: string;
  authMethod: string;
  status: string;
  issuedAt: string;
  lastSeenAt: string;
  expiresAt: string;
  current: boolean;
}

export interface VaccinationRecord {
  id: string;
  vaccineName: string;
  doseLabel: string | null;
  administeredOn: string | null;
  administeredAt: string | null;
  batchNumber: string | null;
  nextDueDate: string | null;
  notes: string | null;
  source: 'SELF_REPORTED' | 'PROVIDER';
  status: 'ACTIVE' | 'REMOVED';
  createdAt: string;
}

// ─── shape adapters: backend document -> legacy page type ───────────────────
const id = (d: any) => String(d?._id ?? d?.id ?? '');
const neoVerifyOf = (nhcId?: string) => {
  const last6 = String(nhcId ?? '').replace(/\D/g, '').slice(-6);
  return last6 ? `NV-${last6}` : '';
};

const toAuthPatient = (p: any) => ({
  userId: p.patientId, patientUserId: p.patientId,
  displayName: p.fullName, fullName: p.fullName,
  nhcId: p.nhcId, neoVerifyId: neoVerifyOf(p.nhcId),
  approvalStatus: p.approvalStatus ?? 'pending',
  phone: p.phone, email: p.email, role: 'PATIENT_USER', institutionId: 'NHC',
});

const toProfile = (d: any): PatientProfile => ({
  id: id(d), patientUserId: d.patientId, nhcId: d.nhcId, fullName: d.fullName ?? '',
  dateOfBirth: d.dob, gender: d.gender, phone: d.phone, email: d.email,
  bloodGroup: d.bloodGroup, allergies: d.allergies ?? [],
  chronicConditions: d.conditions ?? [],
  // The server holds this as one string. It was a hardcoded empty array, so
  // the web showed no medicines however many the patient had written down.
  medicines: d.medicines ?? null,
  surgeries: d.surgeries ?? [], familyHistory: d.familyHistory ?? [],
  avatarUrl: d.avatarUrl ?? null,
  address: d.address, city: d.city, state: d.state, pincode: d.pin,
  // Who to call. All five were dropped, so the web could neither show nor
  // correct the contacts an ambulance crew is given.
  emergencyName: d.emergencyName ?? null,
  emergencyPhone: d.emergencyPhone ?? null,
  emergencyRelation: d.emergencyRelation ?? null,
  secondaryContactName: d.secondaryContactName ?? null,
  secondaryContactPhone: d.secondaryContactPhone ?? null,
  organDonor: d.organDonor === true,
  preferredHospital: d.preferredHospital ?? null,
  insuranceProvider: d.insuranceProvider ?? null,
  insurancePolicyNumber: d.insurancePolicyNumber ?? null,
  smokingStatus: d.smokingStatus ?? null,
  alcoholStatus: d.alcoholStatus ?? null,
  pregnancyStatus: d.pregnancyStatus ?? null,
  disabilityStatus: d.disabilityStatus ?? null,
  preferredLanguage: d.preferredLanguage ?? null,
  abhaId: d.abhaId ?? null,
  // Named as the server names it. It was `abdmLinkedStatus` and never
  // translated back, so every save of it was silently discarded.
  abhaStatus: d.abhaStatus ?? null,
  neoVerifyId: d.neoVerifyId ?? null,
  identityVerified: d.identityVerified === true,
  approvalStatus: d.approvalStatus ?? undefined,
  consents: d.consents ?? undefined,
  preferences: d.preferences ?? undefined,
});

const fromProfilePatch = (data: Partial<PatientProfile>) => {
  const b: Record<string, unknown> = { ...data };
  if (data.dateOfBirth !== undefined) { b.dob = data.dateOfBirth; delete b.dateOfBirth; }
  if (data.pincode !== undefined) { b.pin = data.pincode; delete b.pincode; }
  if (data.chronicConditions !== undefined) { b.conditions = data.chronicConditions; delete b.chronicConditions; }
  /**
   * Facts the SERVER decides. Sending them back is at best ignored and at
   * worst a client asserting its own verification state, so they are dropped.
   */
  for (const key of ['id', 'patientUserId', 'nhcId', 'avatarUrl', 'neoVerifyId',
    'identityVerified', 'approvalStatus'] as const) {
    delete (b as Record<string, unknown>)[key];
  }
  // Callers (e.g. Settings) round-trip the whole fetched profile back on
  // save, not just the fields the user touched — any field the patient
  // never set comes back as null/undefined from GET and must be dropped
  // here, or a PATCH ends up "clearing" fields nobody meant to change.
  for (const key of Object.keys(b)) {
    if (b[key] === null || b[key] === undefined) delete b[key];
  }
  return b;
};

const toAppointment = (d: any) => ({
  id: id(d), appointmentId: id(d), doctorName: d.providerName ?? 'Doctor', speciality: d.specialty,
  // Carried so a reschedule can offer that provider's real published slots.
  providerId: (d.providerId ?? d.provider?.id ?? null) as string | null,
  hospitalName: undefined as string | undefined, appointedAt: d.appointedAt ?? d.createdAt,
  consultationType: d.consultationType ?? 'IN_PERSON', reasonForVisit: d.reasonForVisit,
  // No online payment exists — fees are settled at the visit.
  paymentMode: 'PAY_AT_VISIT', status: d.status ?? 'SCHEDULED',
  tokenNumber: (d.tokenNumber ?? undefined) as string | undefined,
});

const toReminder = (d: any): MedicineReminder => ({
  id: id(d), medicineName: d.medicineName ?? '', dosage: d.dosage ?? undefined,
  frequency: d.frequency ?? 'ONCE_DAILY', times: d.times ?? [],
  startDate: d.startDate ?? d.createdAt ?? '', endDate: d.endDate ?? undefined,
  notes: d.notes ?? undefined, instructions: d.instructions ?? undefined,
  status: d.status ?? 'ACTIVE', prescriptionId: d.prescriptionId ?? undefined,
  doctorName: d.doctorName ?? undefined, doctorNhcId: d.doctorNhcId ?? undefined,
  source: d.source ?? 'MANUAL',
  // MEDICINE | DIET | ROUTINE, and the care plan it belongs to. Neither was
  // mapped, so a diet goal and a daily routine both listed as medicines.
  kind: d.kind ?? 'MEDICINE', groupId: d.groupId ?? undefined,
  missedDoseAlert: d.missedDoseAlert !== false,
  dosesPerDay: typeof d.dosesPerDay === 'number' ? d.dosesPerDay : undefined,
  // Only a real pharmacy order says a medicine was dispensed; without an
  // order number there is nothing to name.
  fulfilment: d.fulfilment?.orderNumber
    ? {
        orderNumber: d.fulfilment.orderNumber,
        pharmacyName: d.fulfilment.pharmacyName ?? undefined,
        pharmacyNh12: d.fulfilment.pharmacyNh12 ?? undefined,
        status: d.fulfilment.status ?? '',
        dispensedAt: d.fulfilment.dispensedAt ?? undefined,
        refillDueOn: d.fulfilment.refillDueOn ?? undefined,
        unitsDispensed: typeof d.fulfilment.unitsDispensed === 'number'
          ? d.fulfilment.unitsDispensed : undefined,
      }
    : undefined,
  adherenceLogs: (d.adherenceLogs ?? []).map((l: any) => ({
    id: l.id, logDate: l.logDate ?? '', taken: l.taken ?? false,
    scheduledTime: l.scheduledTime ?? l.takenAt ?? null,
    takenAt: l.takenAt ?? undefined, skippedNote: l.skippedNote ?? undefined,
    source: l.source ?? undefined,
  })),
});

const toLabSample = (d: any): LabSample => ({
  id: id(d),
  // The LAB's own booking reference. This was minted here as
  // `LB-<last 6 of the mongo id>`, so the code the patient read out to the lab
  // was one the lab had never issued.
  sampleCode: d.appointmentNumber ?? '',
  testName: d.testName ?? (Array.isArray(d.tests) ? d.tests.join(', ') : ''),
  tests: Array.isArray(d.tests) ? d.tests : [],
  testCount: typeof d.testCount === 'number' ? d.testCount : (Array.isArray(d.tests) ? d.tests.length : 0),
  labName: d.labName ?? d.providerName ?? '',
  orderedAt: d.bookedAt ?? d.createdAt ?? '',
  // When the sample is actually due — the one date a tracking page is for,
  // and the one it was not mapping.
  scheduledAt: d.scheduledAt ?? undefined,
  scheduledDate: d.scheduledDate ?? undefined,
  status: d.status ?? 'BOOKED',
  collectionMode: d.collectionMode ?? undefined,
  collectionType: d.collectionType ?? undefined,
  referringDoctorName: d.referringDoctorName ?? undefined,
  notes: d.notes ?? undefined,
  prescriptionId: d.prescriptionId ?? undefined,
});

const toLabReport = (d: any): LabReport => ({
  id: id(d),
  reportNumber: d.reportNumber ?? undefined,
  orderNumber: d.orderNumber ?? undefined,
  // The server sends `testNames[]`; reading `testName` left every report
  // nameless, and `providerName` left every lab called "Lab".
  testNames: Array.isArray(d.testNames) ? d.testNames : [],
  testName: Array.isArray(d.testNames) ? d.testNames.join(', ') : (d.testName ?? ''),
  labName: d.labName ?? d.providerName ?? '',
  reportDate: d.reportedAt ?? d.createdAt ?? '',
  sharedAt: d.sharedAt ?? undefined,
  status: d.status ?? '',
  // The lab's own flag. This was hardcoded to false, so a result the lab had
  // marked critical reached the patient looking like any other.
  isCritical: d.hasCriticalResult === true,
  criticalCount: typeof d.criticalResultCount === 'number' ? d.criticalResultCount : 0,
  patientBookingId: d.patientBookingId ?? undefined,
  // Only the detail payload carries these; the list deliberately does not.
  results: Array.isArray(d.results)
    ? d.results.map((g: any) => ({
        testName: g.testName ?? '',
        parameters: Array.isArray(g.parameters)
          ? g.parameters.map((p: any) => ({
              name: p.name ?? null,
              value: p.value ?? null,
              unit: p.unit ?? null,
              refRange: p.refRange ?? null,
              flag: p.flag ?? 'NORMAL',
              isCritical: p.isCritical === true,
            }))
          : [],
      }))
    : undefined,
});

const toPolicy = (d: any): InsurancePolicy => ({
  id: id(d), providerName: d.providerName ?? '', planName: d.planName ?? undefined,
  policyNumber: d.policyNumber ?? '', coverageAmount: d.coverageAmount ?? 0,
  premiumAmount: d.premiumAmount ?? undefined, validFrom: d.validFrom ?? '', validTo: d.validTo ?? '',
  status: d.status ?? 'ACTIVE', memberId: d.memberId ?? undefined,
  insurerNhcId: d.insurerNhcId ?? undefined, insurerPhone: d.insurerPhone ?? undefined,
  insurerWebsite: d.insurerWebsite ?? undefined,
  // Who says the policy exists, and whether the dates have run out — both are
  // the server's answers, not something the page should re-derive.
  source: d.source ?? 'SELF_REPORTED', verifiedAt: d.verifiedAt ?? undefined,
  documents: Array.isArray(d.documents) ? d.documents : [],
  expired: d.expired === true, daysToExpiry: d.daysToExpiry ?? undefined,
  claims: [],
});

const toClaim = (d: any): InsuranceClaim => ({
  id: id(d), claimNumber: d.claimNumber ?? `CLM-${id(d).slice(-6).toUpperCase()}`,
  // Claim | Preauth, and the policy it is filed against. The claim card read
  // both of these before they were mapped, so a pre-auth was labelled "Claim"
  // and "Policy:" was always blank.
  type: d.type ?? 'Claim', policyNumber: d.policyNumber ?? '',
  claimDate: d.claimDate ?? d.createdAt ?? '', description: d.description ?? undefined,
  serviceName: d.serviceName ?? undefined,
  amount: d.amount ?? 0, status: d.status ?? 'SUBMITTED',
  settledAmount: d.settledAmount ?? undefined, hospitalName: d.hospitalName ?? undefined,
  insurerReference: d.insurerReference ?? undefined, decidedAt: d.decidedAt ?? undefined,
  rejectionReason: d.rejectionReason ?? undefined,
  documents: Array.isArray(d.documents) ? d.documents : [],
});

const toFamily = (d: any): FamilyMember => ({
  id: id(d), name: d.name ?? '', relation: d.relation ?? '',
  dateOfBirth: d.dateOfBirth ?? undefined, gender: d.gender ?? undefined,
  bloodGroup: d.bloodGroup ?? undefined, phone: d.phone ?? undefined,
  isEmergencyContact: d.isEmergencyContact ?? false,
  allergies: [], chronicConditions: [], nhcCardId: d.linkedNhcId,
  isActive: d.status !== 'REMOVED',
});

/**
 * One consent the patient granted, as the SERVER holds it.
 *
 * This used to be mapped onto the device store's `ConsentGrant` so the two
 * could be merged — which meant a grant the server never recorded could still
 * list as ACTIVE, and the fields the server does send (who it binds to, whether
 * it can bind at all) had nowhere to go.
 */
export interface PatientConsent {
  id: string;
  recipientName: string;
  recipientRole: string;
  /** The card id the grant binds by. Null means it binds to nobody. */
  recipientNhcId: string | null;
  scopes: string[];
  /** Those scopes in words, named by the server. */
  scopeLabels: string[];
  /**
   * Whether this grant can take effect at all. False for an older row recorded
   * against a typed name, or one naming a scope this platform does not gate on.
   */
  effective: boolean;
  validUntil: string;
  status: string;
  createdAt: string;
}

const toConsent = (d: any): PatientConsent => ({
  id: id(d),
  recipientName: d.sharedWithName ?? '',
  recipientRole: d.sharedWithRole ?? '',
  recipientNhcId: d.sharedWithNhcId ?? null,
  scopes: d.scopes ?? [],
  scopeLabels: Array.isArray(d.scopeLabels) ? d.scopeLabels : (d.scopes ?? []),
  effective: d.effective !== false,
  validUntil: d.validUntil ?? '',
  status: d.status ?? 'ACTIVE',
  createdAt: d.createdAt ?? '',
});

const toPharmacyOrder = (d: any): PharmacyOrder => ({
  // The server mints and stores the order code; building one from the id meant
  // the code on screen matched nothing the pharmacy could look up.
  id: id(d), orderCode: d.orderCode ?? '',
  providerId: d.providerId ?? undefined,
  pharmacyName: d.providerName ?? 'Pharmacy', deliveryMode: d.deliveryMode ?? 'DELIVERY',
  deliveryAddress: d.deliveryAddress, status: d.status ?? 'PLACED',
  orderedAt: d.createdAt ?? '', totalAmount: d.totalAmount, items: d.items ?? [],
  prescriptionId: d.prescriptionId ?? undefined,
  sentToPharmacy: d.sentToPharmacy ?? false,
  pharmacyOrderNumber: d.pharmacyOrderNumber ?? null,
  pharmacyStatus: d.pharmacyStatus ?? null,
  timeline: d.timeline ?? [],
});

const toTimelineEvent = (e: any): TimelineEvent => ({
  id: e.refId ?? id(e), eventType: e.type ?? 'EVENT', title: e.title ?? '',
  // `detail` is built from the event's own audit metadata — the provider
  // booked with, the policy claimed against. It used to read `status`, which
  // the server sent as the constant 'SUCCESS', so every row on the Health
  // Timeline said SUCCESS underneath it.
  description: e.detail ?? undefined, status: e.status ?? undefined,
  eventDate: e.at ?? '',
});

const toCertificateDoc = (d: any): PatientCertificateDoc => ({
  id: id(d),
  // The issuer's number, and the holder's identity, both from the server.
  // These were `''` and the Mongo id — so an official-looking certificate
  // rendered with a blank name, a blank card id and a database key on it.
  certificateNumber: d.certificateNumber ?? null,
  certificateType: d.certificateType ?? 'CERTIFICATE',
  typeLabel: d.typeLabel ?? undefined,
  patientName: d.patientName ?? null,
  patientNhcId: d.patientNhcId ?? null,
  patientGender: d.patientGender ?? null,
  patientAge: typeof d.patientAge === 'number' ? d.patientAge : null,
  expired: d.expired === true,
  title: d.title ?? '',
  description: d.description ?? undefined, issuerType: d.issuerType ?? 'PROVIDER',
  issuerName: d.issuerName ?? undefined, linkedProviderId: d.linkedProviderId ?? undefined,
  issueDate: d.issueDate ?? d.createdAt ?? '', expiryDate: d.expiryDate ?? undefined,
  status: d.status ?? 'ACTIVE', verificationStatus: d.verificationStatus ?? 'PENDING_NEOVERIFY',
  neoVerifyId: d.neoVerifyId ?? undefined, isShareable: d.isShareable ?? true,
  metadata: d.metadata ?? undefined,
  createdAt: d.createdAt ?? '',
});

const toProvider = (d: any): MarketplaceProvider => ({
  id: id(d), type: (d.type ?? 'DOCTOR') as MarketplaceProvider['type'],
  name: d.name ?? '', slug: d.slug ?? id(d), description: d.description ?? undefined,
  specialties: d.specialty ? [d.specialty] : [],
  address: d.address ?? undefined, city: d.city ?? undefined, state: d.state ?? undefined,
  pincode: d.pincode ?? undefined, latitude: d.geo?.lat, longitude: d.geo?.lng,
  phone: d.phone ?? undefined, email: d.email ?? undefined, website: d.website ?? undefined,
  logoUrl: d.logoUrl ?? undefined, coverImageUrl: d.coverImageUrl ?? undefined,
  rating: d.rating ?? 0, reviewCount: d.reviewCount ?? 0,
  isVerified: !!d.verified, isActive: d.status !== 'INACTIVE', openHours: d.openHours ?? undefined,
  tags: d.tags ?? [], metadata: d.metadata ?? undefined,
  certificates: d.certificates ?? [], pricing: d.pricing ?? [],
  createdAt: d.createdAt ?? '', updatedAt: d.updatedAt ?? '',
  // These four were dropped on the floor: the server has been sending a
  // doctor's registration, training, fees and clinics (and every provider's
  // accreditations) and nothing in the app read them.
  accreditations: d.accreditations ?? [],
  consultModes: d.consultModes ?? [],
  chatNhcId: d.chatNhcId ?? null,
  neoHealthCardId: d.neoHealthCardId ?? d.nhcId ?? null,
  doctor: d.doctor ?? null,
});

const toVaultItem = (d: any): HealthVaultItem => ({
  id: id(d), patientUserId: d.patientId, nhcId: '',
  category: d.category ?? 'REPORT', title: d.title ?? '',
  description: d.notes ?? undefined, fileName: d.fileName ?? undefined,
  verificationStatus: 'INTERNAL', tags: [], isActive: d.status !== 'DELETED',
  createdAt: d.createdAt ?? '',
});

const toConversation = (d: any): PatientConversation => ({
  id: id(d), nhcId: '', conversationType: d.conversationType ?? 'GENERAL',
  title: d.title ?? undefined, participantNhcIds: d.participantNhcIds ?? [],
  participantNames: d.participantNames, isGroup: (d.participantNhcIds?.length ?? 0) > 2, unreadCount: 0,
  status: d.status ?? 'ACTIVE', lastMessageAt: d.lastMessageAt ?? undefined,
  createdAt: d.createdAt ?? '',
});

// senderNhcId must reflect the REAL sender — MyChat.tsx computes
// `isMe = msg.senderNhcId === nhcId` to decide which side of the thread a
// message renders on; hardcoding '' (or always 'You') broke that for every
// backend-sourced message.
const toChatMessage = (d: any): PatientChatMessage => ({
  id: id(d), conversationId: d.threadId ?? '', senderNhcId: d.senderNhcId ?? '',
  senderName: d.senderName ?? 'Unknown', senderRole: d.senderRole ?? 'PATIENT',
  messageType: (d.messageType ?? 'TEXT') as PatientChatMessage['messageType'], content: d.content ?? '',
  metadata: d.metadata ?? undefined,
  consentRequired: false, consentGranted: d.metadata?.consentGranted ?? true,
  deliveryStatus: 'SENT', createdAt: d.createdAt ?? '',
});

// The shared consultation (`/telemedicine/consultations`) — the same session
// the doctor sees. The old list read a patient-only record that no doctor was
// ever linked to.
const toTeleSession = (d: any): PatientTelemedicineSession => ({
  id: id(d), nhcId: '', doctorNhcId: '', doctorName: d.doctorName ?? d.providerName ?? 'Doctor',
  appointmentId: d.appointmentId ?? undefined, sessionType: 'VIDEO', status: d.status ?? 'SCHEDULED',
  scheduledAt: d.scheduledAt ?? d.createdAt ?? '', recordingConsent: false, healthHistoryConsent: d.consentStatus === 'granted',
});

// ─── AUTH — wired to /api/patient/auth/* (shapes per patientAuthFlow.ts) ─────
export const patientAuthApi = {
  register: async (data: {
    fullName: string; phone?: string; email?: string; dob?: string; gender?: string;
    bloodGroup?: string; address?: string; city?: string; state?: string; pin?: string;
    emergencyName?: string; emergencyPhone?: string; emergencyRelation?: string;
    allergies?: string[]; conditions?: string[]; medicines?: string; surgeries?: string[];
    familyHistory?: string[];
    abhaId?: string; abhaStatus?: string; password?: string;
    consents?: Record<string, boolean>;
  }) => {
    const r = await http.post<any>('/auth/signup', data);
    if (r.token) { setPatientToken(r.token); saveToken(r.token); }
    if (r.patient) savePatientSummary({ patientId: r.patient.patientId, nhcId: r.patient.nhcId, fullName: r.patient.fullName, phone: r.patient.phone, email: r.patient.email });
    return {
      ...r, nhcId: r.patient?.nhcId, neoVerifyId: neoVerifyOf(r.patient?.nhcId),
      approvalStatus: r.patient?.approvalStatus ?? 'pending',
      patient: r.patient ? toAuthPatient(r.patient) : undefined,
    };
  },

  /**
   * Start a password reset — `POST /auth/forgot-password`.
   *
   * The server refuses honestly when no account matches rather than
   * pretending a code went out, and that refusal is passed straight through.
   */
  forgotPassword: async (channel: 'phone' | 'email', identifier: string) => {
    const r = await http.post<{ otpRequestId: string; expiresInSeconds?: number; devCode?: string }>(
      '/auth/forgot-password',
      { channel, identifier: identifier.trim() },
    );
    return {
      ...r,
      // Only ever surfaced when this build explicitly opts into demo auth.
      demoOtp: import.meta.env?.VITE_ENABLE_DEMO_AUTH === 'true' ? r.devCode : undefined,
    };
  },

  /**
   * Finish a password reset — `POST /auth/reset-password`. No session comes
   * back: every device is signed out and the patient signs in again.
   */
  resetPassword: (data: {
    otpRequestId: string;
    code: string;
    channel: 'phone' | 'email';
    identifier: string;
    newPassword: string;
  }) => http.post<{ reset: boolean }>('/auth/reset-password', {
    otpRequestId: data.otpRequestId,
    code: data.code.trim(),
    channel: data.channel,
    identifier: data.identifier.trim(),
    newPassword: data.newPassword,
  }),

  /**
   * The whole second factor on this account — `GET /auth/2fa`.
   *
   * The Security screen could only show whether the one-time-code step was on.
   * The authenticator half (setup, confirm, remove, recovery codes, trusted
   * devices) exists on the server and had no surface here at all.
   */
  twoFactorStatus: () => http.get<TwoFactorStatus>('/auth/2fa'),

  /** Begin authenticator setup. The secret is shown ONCE and cannot be re-read. */
  totpSetup: () => http.post<{ secret: string; uri: string; note: string }>('/auth/2fa/totp/setup'),

  /** Prove the authenticator produces the right code, which switches it on. */
  totpConfirm: (code: string) => http.post<TwoFactorStatus>('/auth/2fa/totp/confirm', { code: code.trim() }),

  /** Remove the authenticator; `proof` is a current code or a recovery code. */
  totpRemove: (proof: string) => http.post<TwoFactorStatus>('/auth/2fa/totp/remove', { proof: proof.trim() }),

  /** New recovery codes. Shown once, stored hashed — the old set stops working. */
  generateRecoveryCodes: () => http.post<{ codes: string[]; note: string }>('/auth/2fa/recovery-codes'),

  /** Trust this browser so it is not asked for a second factor every time. */
  trustDevice: (label: string) => http.post<TwoFactorStatus>('/auth/2fa/trusted-devices', { label }),

  revokeTrustedDevice: (deviceId: string) =>
    http.del<TwoFactorStatus>(`/auth/2fa/trusted-devices/${encodeURIComponent(deviceId)}`),

  checkAvailability: async (data: { phone?: string; email?: string }) => {
    const params: Record<string, string> = {};
    if (data.phone) params.phone = data.phone;
    if (data.email) params.email = data.email;
    return http.get<{ phoneExists: boolean; emailExists: boolean }>('/auth/check-availability', params);
  },

  requestOtp: async (channel: 'phone' | 'email', identifier: string) => {
    const r = await http.post<any>('/auth/send-otp', { channel, identifier });
    // devCode is only ever meant to exist in mock/dev backend responses. Never
    // surface it into app state unless this environment explicitly opts into
    // demo auth — otherwise a mock provider left on by mistake in a deployed
    // environment would let the client bypass real OTP verification.
    const demoOtp = import.meta.env?.VITE_ENABLE_DEMO_AUTH === 'true' ? r.devCode : undefined;
    return { ...r, demoOtp };
  },

  verifyOtp: async (data: { otpRequestId?: string; code: string; channel?: string; identifier?: string }) => {
    const r = await http.post<any>('/auth/verify-otp', data);
    if (r.token) { setPatientToken(r.token); saveToken(r.token); }
    if (r.patient) savePatientSummary({ patientId: r.patient.patientId, nhcId: r.patient.nhcId, fullName: r.patient.fullName, phone: r.patient.phone, email: r.patient.email });
    return {
      ...r, patient: r.patient ? toAuthPatient(r.patient) : undefined,
      approvalStatus: r.patient?.approvalStatus ?? (r.token ? 'approved' : undefined),
    };
  },

  login: async (data: { phone?: string; email?: string; password?: string }) => {
    // No password -> steer the UI to the OTP path (backend passwords are optional).
    if (!data.password) {
      return {
        requiresVerification: true, nextStep: 'otp',
        channel: data.phone ? 'phone' : 'email', identifier: data.phone ?? data.email,
      };
    }
    const r = await http.post<any>('/auth/login', data);
    if (r.token) { setPatientToken(r.token); saveToken(r.token); }
    if (r.patient) savePatientSummary({ patientId: r.patient.patientId, nhcId: r.patient.nhcId, fullName: r.patient.fullName, phone: r.patient.phone, email: r.patient.email });
    return { ...r, patient: r.patient ? toAuthPatient(r.patient) : undefined, approvalStatus: r.patient?.approvalStatus ?? 'pending' };
  },

  me: async () => {
    const r = await http.get<any>('/me');
    return toAuthPatient(r);
  },

  logout: async () => {
    try { await http.post<any>('/auth/logout'); } finally { clearToken(); }
    return { loggedOut: true };
  },

  // Real credential update; the server revokes all sessions on success, so
  // callers must clear the local token and send the user back to login.
  changePassword: (data: { currentPassword?: string; newPassword: string }) =>
    http.post<{ changed: boolean; reauthRequired: boolean }>('/auth/change-password', data),

  // Security surface — all real: overview flags, OTP-based two-factor, and
  // per-device sessions recorded at token issuance (revocable by jti).
  getSecurityOverview: () =>
    http.get<{ twoFactorEnabled: boolean; passwordSet: boolean; phone: string | null; email: string | null }>('/auth/security'),
  setTwoFactor: (enabled: boolean) =>
    http.patch<{ twoFactorEnabled: boolean }>('/auth/two-factor', { enabled }),
  getSessions: () => http.get<PatientSessionInfo[]>('/auth/sessions'),
  requestAccountDeletion: () =>
    http.post<{ requested: boolean; requestedAt: string; alreadyPending: boolean }>('/auth/account-deletion-request'),
  revokeSession: (jti: string) => http.del<{ revoked: boolean }>(`/auth/sessions/${encodeURIComponent(jti)}`),
  revokeOtherSessions: () => http.post<{ revoked: number }>('/auth/sessions/revoke-others'),

  approvalStatus: async (_patientUserId?: string) => {
    const r = await http.get<any>('/me');
    return { approvalStatus: r.approvalStatus ?? 'pending', nhcId: r.nhcId };
  },
};

// ─── PATIENT DATA — wired to /api/patient/* with adapters ────────────────────
export const patientApi = {
  getDashboard: async (): Promise<DashboardSummary> => {
    const s = await http.get<any>('/home/summary');
    const next = s.nextAppointment ? [toAppointment(s.nextAppointment)] : [];
    return {
      profile: { id: '', nhcId: s.patient?.nhcId ?? '', fullName: s.patient?.fullName ?? 'Patient' },
      upcomingAppointments: next,
      activeReminders: (s.activeReminders ?? []).map(toReminder),
      pendingLabSamples: [],
      activePolicies: [],
      recentTimeline: (s.recentTimeline ?? []).map(toTimelineEvent),
      activeConsents: 0,
      pendingFollowUps: [], familyMembers: [],
      unreadNotifications: s.counts?.unreadNotifications ?? 0,
      stats: {
        upcomingAppointmentCount: s.counts?.upcomingAppointments ?? 0,
        activeMedicineCount: s.counts?.activeMedicines ?? 0,
        pendingLabCount: s.counts?.labBookings ?? 0,
        activePolicyCount: s.counts?.insurancePolicies ?? 0,
        activeConsentCount: 0, familyMemberCount: 0,
        unreadNotificationCount: s.counts?.unreadNotifications ?? 0,
      },
    };
  },
  getNotifications: (): Promise<PatientNotificationRow[]> => http.get('/notifications'),
  /** Mark one notification read on the server (the badge counts the server's copy). */
  markNotificationRead: (idStr: string): Promise<PatientNotificationRow> =>
    http.patch(`/notifications/${idStr}/read`),
  markAllNotificationsRead: (): Promise<{ updated: boolean }> =>
    http.patch('/notifications/read-all'),
  getProfile: async (): Promise<PatientProfile> => toProfile(await http.get<any>('/profile')),

  /**
   * The patient's profile photo — `POST` / `DELETE /profile/photo`, and the
   * file itself from `GET /profile/photo/:file`.
   *
   * All three existed and this app used none of them: a photo set in the app
   * was invisible on the web and could not be changed there. The file route is
   * authenticated, so an `<img src>` cannot fetch it — it comes back as a blob
   * and is rendered from an object URL, the same way the app sends its own
   * auth header.
   */
  uploadProfilePhoto: async (file: File): Promise<PatientProfile> => {
    const form = new FormData();
    form.append('file', file);
    return toProfile(await http.upload<any>('/profile/photo', form));
  },
  deleteProfilePhoto: async (): Promise<PatientProfile> =>
    toProfile(await http.del<any>('/profile/photo')),
  profilePhotoBlob: (avatarUrl: string): Promise<Blob> =>
    // The server sends an absolute API path; `http.blob` wants one relative to
    // the patient base.
    http.blob(avatarUrl.replace(/^\/api\/patient/, '')),
  updateProfile: async (data: Partial<PatientProfile>): Promise<PatientProfile> =>
    toProfile(await http.patch<any>('/profile', fromProfilePatch(data))),
  getEmergencyProfile: async (): Promise<EmergencyProfile> => {
    const p = await http.get<any>('/profile');
    return {
      id: id(p), bloodGroup: p.bloodGroup, allergies: p.allergies ?? [],
      currentMedications: [], chronicConditions: p.conditions ?? [],
      emergencyContactName: p.emergencyName, emergencyContactPhone: p.emergencyPhone,
      emergencyContactRelation: p.emergencyRelation,
      secondaryContactName: p.secondaryContactName, secondaryContactPhone: p.secondaryContactPhone,
      preferredHospital: p.preferredHospital, insuranceProvider: p.insuranceProvider,
      insurancePolicyNumber: p.insurancePolicyNumber, organDonor: p.organDonor ?? false,
    };
  },
  updateEmergencyProfile: async (data: Partial<EmergencyProfile>): Promise<EmergencyProfile> => {
    const body: Record<string, unknown> = {
      emergencyName: data.emergencyContactName, emergencyPhone: data.emergencyContactPhone,
      emergencyRelation: data.emergencyContactRelation,
      secondaryContactName: data.secondaryContactName, secondaryContactPhone: data.secondaryContactPhone,
      preferredHospital: data.preferredHospital, insuranceProvider: data.insuranceProvider,
      insurancePolicyNumber: data.insurancePolicyNumber, organDonor: data.organDonor,
      ...(data.bloodGroup ? { bloodGroup: data.bloodGroup } : {}),
      ...(data.allergies ? { allergies: data.allergies } : {}),
      ...(data.chronicConditions ? { conditions: data.chronicConditions } : {}),
    };
    for (const key of Object.keys(body)) {
      if (body[key] === undefined || body[key] === null) delete body[key];
    }
    await http.patch<any>('/profile', body);
    return patientApi.getEmergencyProfile();
  },

  /**
   * The patient's own history.
   *
   * `type` and `exclude` each take one type or a comma-separated set, and the
   * response carries `types` — the vocabulary the server can actually return,
   * so the filter chips are built from it instead of from a guess.
   */
  getTimeline: async (params?: { type?: string; exclude?: string; page?: number; limit?: number }): Promise<{ events: TimelineEvent[]; total: number; types: string[] }> => {
    const r = await http.get<any>('/home/timeline', params as any);
    const events = (r.events ?? []).map(toTimelineEvent);
    return { events, total: r.total ?? events.length, types: r.types ?? [] };
  },

  triggerSOS: (data: { emergencyType: string; latitude?: number; longitude?: number; notes?: string }) =>
    http.post<any>('/emergency/request', {
      emergencyType: data.emergencyType, notes: data.notes, severity: 'HIGH',
      ...(data.latitude !== undefined ? { location: { lat: data.latitude, lng: data.longitude } } : {}),
    }),

  /**
   * The consent screen in one call — standing connections, the requests
   * waiting on the patient, and the ones already answered.
   *
   * A doctor could ask for access and the web app had no surface to answer:
   * it only ever listed the grants the patient started themselves. The
   * request sat unanswered with the doctor's side waiting on it.
   */
  consentOverview: () => http.get<ConsentOverview>('/consent-overview'),

  /** Answer a pending request. Both write a real, audited decision. */
  allowConsentRequest: (idStr: string) => http.post<unknown>(`/consent-requests/${idStr}/allow`),
  denyConsentRequest: (idStr: string) => http.post<unknown>(`/consent-requests/${idStr}/deny`),

  getConsents: async (): Promise<PatientConsent[]> => (await http.get<any[]>('/consents')).map(toConsent),
  /**
   * What a patient may share, and what each scope really opens —
   * `GET /consent-scopes`.
   *
   * Server-driven because these are ENFORCED: each one gates a named section of
   * the doctor's patient view. This app offered six words of its own invention
   * that no gate anywhere read, so a patient who ticked "Lab Reports" alone
   * granted exactly as much as one who ticked everything.
   */
  consentScopes: () =>
    http.get<{ scope: string; label: string; description: string }[]>('/consent-scopes'),

  /**
   * Who a NeoHealthCard id belongs to — `GET /consent-recipients/:nhcId`.
   *
   * A consent binds by card id and nothing else. This app posted a TYPED name
   * with no id at all, so every grant it created listed as ACTIVE and opened
   * nothing for anybody.
   */
  findConsentRecipient: (nhcId: string) =>
    http.get<{ nhcId: string; name: string; role: string; detail: string | null; verified: boolean }>(
      `/consent-recipients/${encodeURIComponent(nhcId.trim())}`,
    ),

  createConsent: async (data: { sharedWithName: string; sharedWithRole: string; sharedWithNhcId: string; scopes: string[]; validHours?: number }): Promise<PatientConsent> =>
    toConsent(await http.post<any>('/consents', data)),
  revokeConsent: async (idStr: string): Promise<PatientConsent> =>
    toConsent(await http.patch<any>(`/consents/${idStr}/revoke`)),

  /**
   * Reviews of a provider, and this patient's own.
   *
   * The server labels a review from someone who actually had a visit — that
   * label is its call, computed from the appointment record, and is never
   * asserted here. `ratingIsFromReviews` says whether the star average has
   * any written reviews behind it at all.
   */
  reviews: {
    list: (idOrSlug: string, limit?: number) =>
      http.get<ProviderReviews>(`/providers/${idOrSlug}/reviews`, limit ? { limit } : undefined),
    mine: (idOrSlug: string) =>
      http.get<{ review: MyProviderReview | null; canReview: boolean }>(`/providers/${idOrSlug}/reviews/mine`),
    /** Leaving one twice edits it; the server upserts on (provider, patient). */
    submit: (idOrSlug: string, data: { rating: number; title?: string; comment?: string }) =>
      http.post<ProviderReviews>(`/providers/${idOrSlug}/reviews`, data),
    remove: (idOrSlug: string) => http.del<ProviderReviews>(`/providers/${idOrSlug}/reviews`),
  },

  /**
   * The patient's own audit trail — every recorded touch of their record,
   * including their own actions and the ones written against a lab order or
   * an ambulance trip, which `/access-logs` (who ELSE touched it) can never
   * show.
   */
  accessTrail: (limit?: number) =>
    http.get<AccessTrail>('/access-trail', limit ? { limit } : undefined),

  /** Sign-ins, password and 2FA changes, devices — the account's own history. */
  securityEvents: () => http.get<SecurityEvents>('/auth/security-events'),

  /**
   * Lab tests a doctor ordered for this patient (`GET /lab/orders`). Real
   * requests raised in the doctor's panel — nothing on the web read them, so
   * a patient whose doctor ordered a test saw no sign of it.
   */
  doctorLabOrders: () => http.get<DoctorLabOrder[]>('/lab/orders'),

  verifyCard: async (nhcId: string): Promise<{ valid: boolean; patient?: { fullName: string; nhcId: string; verified: boolean } }> =>
    http.post<any>('/card/verify', { nhcId }),

  getMedicines: async (): Promise<MedicineReminder[]> => (await http.get<any[]>('/medicine-reminders')).map(toReminder),
  getTodayMedicines: async (): Promise<MedicineReminder[]> => (await http.get<any[]>('/medicines')).map(toReminder),
  createMedicine: async (data: any): Promise<MedicineReminder> =>
    toReminder(await http.post<any>('/medicine-reminders', data)),
  createRemindersFromPrescription: async (prescriptionId: string): Promise<MedicineReminder[]> =>
    (await http.post<any[]>(`/medicine-reminders/from-prescription/${prescriptionId}`)).map(toReminder),
  updateMedicine: async (idStr: string, data: any): Promise<MedicineReminder> =>
    toReminder(await http.patch<any>(`/medicine-reminders/${idStr}`, data)),
  deleteMedicine: (idStr: string) => http.patch<any>(`/medicine-reminders/${idStr}`, { active: false, status: 'ENDED' }),
  logAdherence: (idStr: string, data: { taken: boolean; scheduledTime?: string; skippedNote?: string; source?: string }) =>
    http.post(`/medicine-reminders/${idStr}/log`, data),

  getInsurancePolicies: async (): Promise<InsurancePolicy[]> => (await http.get<any[]>('/insurance')).map(toPolicy),
  getInsuranceClaims: async (): Promise<InsuranceClaim[]> => (await http.get<any[]>('/claims')).map(toClaim),
  /**
   * Records a policy the patient holds, stored SELF_REPORTED — nothing here
   * confirms it with an insurer. This route has always existed; without it a
   * patient with no policy could never file a claim, because the server checks
   * the claim's policy number against their own policies.
   */
  createInsurancePolicy: async (data: {
    providerName: string; policyNumber: string; coverageAmount: number;
    validFrom: string; validTo: string;
    planName?: string; memberId?: string; premiumAmount?: number;
    insurerPhone?: string; insurerWebsite?: string; insurerNhcId?: string;
  }): Promise<InsurancePolicy> => toPolicy(await http.post<any>('/insurance', data)),
  deleteInsurancePolicy: (policyId: string) => http.del<any>(`/insurance/${policyId}`),
  /**
   * `serviceName` and `hospitalName` are fields on the claim schema, so they
   * travel as themselves instead of being mashed into `description` where
   * nothing can read them back.
   */
  createInsuranceClaim: async (data: {
    policyNumber: string; type?: 'Claim' | 'Preauth'; amount: number; description: string;
    serviceName?: string; hospitalName?: string; documents?: string[];
  }): Promise<InsuranceClaim> => toClaim(await http.post<any>('/claims', data)),

  getFamily: async (): Promise<FamilyMember[]> => (await http.get<any[]>('/family')).map(toFamily),
  addFamily: async (data: any): Promise<FamilyMember> => toFamily(await http.post<any>('/family', data)),
  updateFamily: async (idStr: string, data: any): Promise<FamilyMember> =>
    toFamily(await http.patch<any>(`/family/${idStr}`, data)),
  removeFamily: (idStr: string) => http.patch<any>(`/family/${idStr}`, { status: 'REMOVED' }),

  getLabSamples: async (): Promise<LabSample[]> => (await http.get<any[]>('/lab/bookings')).map(toLabSample),
  getLabReports: async (): Promise<LabReport[]> => (await http.get<any[]>('/lab/reports')).map(toLabReport),
  /**
   * One report WITH the lab's findings — `GET /lab/reports/:id`.
   *
   * The list deliberately omits `results`; this is the only way to see what
   * the lab actually measured. Nothing in either app fetched it, so a patient
   * was told a result was critical and could not see which value it was.
   */
  getLabReport: async (idStr: string): Promise<LabReport> =>
    toLabReport(await http.get<any>(`/lab/reports/${idStr}`)),
  /**
   * Where the patient's lab orders have got to. This is the LAB's own
   * pipeline — the stage dates come from the timeline its staff stamp, not
   * from a booking status nothing advances. It carries no results.
   */
  getLabTracking: async (): Promise<LabTracking[]> => await http.get<LabTracking[]>('/lab/tracking'),

  getPharmacyOrders: async (): Promise<PharmacyOrder[]> => (await http.get<any[]>('/medicine-orders')).map(toPharmacyOrder),
  createPharmacyOrder: async (data: any): Promise<PharmacyOrder> =>
    toPharmacyOrder(await http.post<any>('/medicine-orders', {
      providerId: data.providerId, providerName: data.pharmacyName ?? data.providerName,
      items: data.items ?? [], deliveryMode: data.deliveryMode, deliveryAddress: data.deliveryAddress,
      totalAmount: data.totalAmount,
    })),
  cancelPharmacyOrder: async (idStr: string): Promise<PharmacyOrder> =>
    toPharmacyOrder(await http.patch<any>(`/medicine-orders/${idStr}/cancel`)),

  // Real endpoint, honestly empty until a provider panel issues follow-ups.
  getFollowUps: () => http.get<FollowUp[]>('/follow-ups'),

  // Prescriptions issued by doctors via the doctor panel (signed only).
  getPrescriptions: () => http.get<PrescriptionRecord[]>('/prescriptions'),
  /**
   * Imaging orders are placed by a doctor or hospital; the patient reads their
   * own (`GET /imaging/orders`) and sees a report once the centre releases it.
   */
  getImagingOrders: () => http.get<ImagingOrderRecord[]>('/imaging/orders'),

  /**
   * One imaging order in full — the radiologist's impression once released,
   * the order's timeline, and the safety notes the centre recorded. The app
   * only ever listed orders, so a released report could not be read here.
   */
  getImagingOrder: (idStr: string) => http.get<ImagingOrderDetail>(`/imaging/orders/${idStr}`),
  getPrescription: (idStr: string) => http.get<PrescriptionRecord>(`/prescriptions/${idStr}`),

  // Vaccination records — self-reported entries saved to the backend.
  getVaccinations: () => http.get<VaccinationRecord[]>('/vaccinations'),
  addVaccination: (data: { vaccineName: string; doseLabel?: string; administeredOn?: string; administeredAt?: string; batchNumber?: string; nextDueDate?: string; notes?: string }) =>
    http.post<VaccinationRecord>('/vaccinations', data),
  updateVaccination: (idStr: string, data: Record<string, unknown>) =>
    http.patch<VaccinationRecord>(`/vaccinations/${idStr}`, data),

  getPreConsultation: async (appointmentId: string) => {
    const r = await http.post<any>('/neoai/summarize-records', { appointmentId });
    return { summary: r.reply, disclaimer: r.disclaimer, informationalOnly: true };
  },

  // `useRecords` mirrors the "Use my NHC data" consent toggle — the server
  // derives the record context itself; the client never sends record data.
  askNeoAI: (message: string, useRecords?: boolean) => http.post<any>('/neoai/chat', { message, useRecords }),

  // Marketplace — wired to /providers
  getMarketplaceProviders: async (params?: { type?: string; city?: string; verified?: string; search?: string; page?: number; limit?: number }): Promise<{ providers: MarketplaceProvider[]; total: number }> => {
    const r = params?.search
      ? await http.get<any>('/providers/search', { q: params.search })
      : await http.get<any>('/providers', params as any);
    const providers = (r.providers ?? []).map(toProvider);
    return { providers, total: r.total ?? providers.length };
  },
  getMarketplaceProvider: async (slug: string): Promise<MarketplaceProvider> =>
    toProvider(await http.get<any>(`/providers/${slug}`)),
  // Per-type totals come from the server's `total` (count query), never from
  // counting one page of results — page-size-capped numbers looked like
  // network-wide totals.
  getMarketplaceStats: async (): Promise<MarketplaceStats> => {
    const count = async (type: string) => {
      try {
        const r = await http.get<any>('/providers', { type, limit: 1 });
        return r.total ?? (r.providers ?? []).length;
      } catch { return 0; }
    };
    const [doctors, labs, hospitals, pharmacies, ambulances] = await Promise.all([
      count('DOCTOR'), count('LAB'), count('HOSPITAL'), count('PHARMACY'), count('AMBULANCE'),
    ]);
    return { doctors, labs, hospitals, pharmacies, ambulances };
  },

  // Marketplace bookings — appointments/lab wired; pharmacy/ambulance mapped
  /**
   * The provider's REAL published openings (`GET /providers/:idOrSlug/slots`).
   * `hasSchedule: false` means this doctor publishes none — the booking
   * screens then ask for a preferred time and say so, instead of inventing a
   * grid of times nobody has checked.
   */
  getProviderSlots: (providerIdOrSlug: string, days?: number): Promise<ProviderSlotsResponse> =>
    http.get(`/providers/${encodeURIComponent(providerIdOrSlug)}/slots`, days ? { days } : undefined),
  /**
   * Book an appointment. Passing `slotId` takes that published seat on the
   * server (capacity is decremented); without one the server records the
   * requested time for the clinic to confirm.
   */
  bookDoctor: async (data: { providerId: string; appointedAt: string; consultationType?: string; reasonForVisit?: string; paymentMode?: string; slotId?: string }) => {
    const p = await http.get<any>(`/providers/${data.providerId}`).catch(() => null);
    return toAppointment(await http.post<any>('/appointments', {
      providerId: data.providerId, providerName: p?.name, specialty: p?.specialty,
      appointedAt: data.appointedAt, consultationType: data.consultationType === 'VIDEO' ? 'VIDEO' : 'IN_PERSON',
      reasonForVisit: data.reasonForVisit,
      ...(data.slotId ? { slotId: data.slotId } : {}),
    }));
  },
  getAppointments: async (): Promise<ReturnType<typeof toAppointment>[]> =>
    (await http.get<any[]>('/appointments')).map(toAppointment),
  cancelAppointment: async (idStr: string): Promise<ReturnType<typeof toAppointment>> =>
    toAppointment(await http.patch<any>(`/appointments/${idStr}/cancel`)),

  /**
   * Move an appointment. The web app could only cancel, so a patient who
   * needed a different time had to cancel and rebook — losing their place
   * and, where the doctor publishes a schedule, their seat.
   *
   * Passing `slotId` takes that published seat; without one the server
   * records the requested time for the clinic to confirm.
   */
  rescheduleAppointment: async (
    idStr: string,
    data: { appointedAt: string; slotId?: string },
  ): Promise<ReturnType<typeof toAppointment>> =>
    toAppointment(await http.patch<any>(`/appointments/${idStr}/reschedule`, {
      appointedAt: data.appointedAt,
      ...(data.slotId ? { slotId: data.slotId } : {}),
    })),
  bookLab: async (data: { providerId: string; testName: string; collectionMode?: string; notes?: string }) => {
    const p = await http.get<any>(`/providers/${data.providerId}`).catch(() => null);
    return toLabSample(await http.post<any>('/lab/bookings', {
      providerId: data.providerId, providerName: p?.name, testName: data.testName,
      collectionMode: data.collectionMode === 'HOME' ? 'HOME' : 'LAB', notes: data.notes,
    }));
  },
  bookPharmacy: async (data: { providerId: string; items: any[]; deliveryMode?: string; deliveryAddress?: string; totalAmount?: number }) => {
    const p = await http.get<any>(`/providers/${data.providerId}`).catch(() => null);
    return toPharmacyOrder(await http.post<any>('/medicine-orders', {
      providerId: data.providerId, providerName: p?.name, items: data.items,
      deliveryMode: data.deliveryMode === 'PICKUP' ? 'PICKUP' : 'DELIVERY',
      deliveryAddress: data.deliveryAddress, totalAmount: data.totalAmount,
    }));
  },
  bookAmbulance: (data: { providerId: string; pickupAddress?: string; emergencyType?: string; notes?: string }) =>
    http.post<any>('/emergency/request', {
      emergencyType: data.emergencyType ?? 'AMBULANCE', severity: 'HIGH',
      notes: [data.notes, data.pickupAddress ? `Pickup: ${data.pickupAddress}` : ''].filter(Boolean).join(' · '),
    }),

  // Documents & Certificates — wired to the real /certificates backend
  // (read-only: certificates are issued to the patient by providers).
  getCertificates: async (params?: { type?: string; status?: string }): Promise<PatientCertificateDoc[]> =>
    (await http.get<any[]>('/certificates', params as any)).map(toCertificateDoc),
  /**
   * Every certificate type this platform issues, with its label —
   * `GET /certificates/types`.
   *
   * Server-driven so the filter chips cannot drift from the enum. This app's
   * own list offered six types nobody issues and had no entry for
   * MEDICAL_CERTIFICATE, which is what a doctor-signed fitness certificate is.
   */
  getCertificateTypes: () =>
    http.get<{ type: string; label: string }[]>('/certificates/types'),
  getCertificate: async (idStr: string): Promise<PatientCertificateDoc> =>
    toCertificateDoc(await http.get<any>(`/certificates/${idStr}`)),
  // Deliberately unbuilt on the backend: fabricated demo certificates must
  // never enter the real certificate store. DocumentTemplates.tsx catches
  // the 404 and shows an error instead of generating.

  getHealthVault: async (params?: { category?: string }): Promise<HealthVaultItem[]> =>
    (await http.get<any[]>('/health-vault/documents', params as any)).map(toVaultItem),
  getHealthVaultItem: async (idStr: string): Promise<HealthVaultItem> => {
    const r = await http.get<any>(`/health-vault/documents/${idStr}`);
    return toVaultItem(r.document ?? r);
  },
  // Real uploads — file lands on the backend's disk store, metadata in Mongo.
  uploadHealthVaultDocument: async (data: { file: File; title: string; category?: string; notes?: string }): Promise<HealthVaultItem> => {
    const form = new FormData();
    form.append('file', data.file);
    form.append('title', data.title);
    if (data.category) form.append('category', data.category);
    if (data.notes) form.append('notes', data.notes);
    return toVaultItem(await http.upload<any>('/health-vault/documents', form));
  },
  deleteHealthVaultDocument: (idStr: string) => http.del<any>(`/health-vault/documents/${idStr}`),
  getHealthVaultFileBlob: (idStr: string): Promise<Blob> => http.blob(`/health-vault/documents/${idStr}/file`),

  // Document shares are consent grants scoped `document:<id>` — read them
  // back from the same real store they are written to.
  getDocumentShares: async (): Promise<DocumentShare[]> => {
    const consents = await http.get<any[]>('/consents');
    return consents
      .filter((c) => (c.scopes ?? []).some((s: string) => String(s).startsWith('document:')))
      .map((c) => ({
        id: id(c),
        documentId: String((c.scopes ?? []).find((s: string) => String(s).startsWith('document:')) ?? '').slice('document:'.length),
        sharedWithName: c.sharedWithName ?? '', sharedWithRole: c.sharedWithRole ?? '',
        status: c.status ?? 'ACTIVE', validUntil: c.validUntil ?? '', createdAt: c.createdAt ?? '',
      } as unknown as DocumentShare));
  },
  shareDocument: (certId: string, data: { sharedWithName: string; sharedWithRole: string; sharedWithInstitution?: string; validHours?: number }) =>
    http.post<any>('/consents', {
      sharedWithName: data.sharedWithName, sharedWithRole: data.sharedWithRole,
      scopes: [`document:${certId}`], validHours: data.validHours,
    }),
  revokeShare: (shareId: string) => http.patch<any>(`/consents/${shareId}/revoke`),
  getDocumentAuditLogs: async (): Promise<DocumentAuditLog[]> => {
    const r = await http.get<any>('/access-logs');
    return (r.logs ?? []).map((l: any) => ({
      id: id(l), nhcId: '', action: l.action ?? 'VIEW', resourceType: l.resourceType ?? '',
      resourceId: l.resourceId ?? '', actorName: l.actorName, actorRole: l.actorRole, performedAt: l.at ?? '',
    }));
  },
  // Previews render straight from the real record — there is no separate
  // "preview" endpoint, and none is needed.
  getCertificatePreview: async (idStr: string) => toCertificateDoc(await http.get<any>(`/certificates/${idStr}`)),
  getDocumentPreview: async (idStr: string) => {
    const r = await http.get<any>(`/health-vault/documents/${idStr}`);
    return r.document ?? r;
  },
  // Sharing a document IS a consent grant — one real record, no side channel.
  shareFromPreview: (idStr: string, data: { sharedWithName: string; sharedWithRole: string; sharedWithInstitution?: string; validHours?: number }) =>
    http.post<any>('/consents', {
      sharedWithName: data.sharedWithName, sharedWithRole: data.sharedWithRole,
      scopes: [`document:${idStr}`], validHours: data.validHours,
    }),

  // Communication — chat threads and groups are real Conversations; calls
  // are real too, but only cover telemedicine session history since there's
  // no ad-hoc voice/video calling feature yet.
  getCommSummary: async (): Promise<CommSummary> => {
    const threads = (await http.get<any[]>('/chat/threads')).map(toConversation);
    return {
      totalUnread: 0, missedCalls: 0, activeChats: threads.length, groups: 0,
      upcomingTelemedicine: 0, recentConversations: threads.slice(0, 5), recentCalls: [], upcomingSessions: [],
    };
  },
  getConversations: async (params?: { type?: string; status?: string }): Promise<PatientConversation[]> =>
    (await http.get<any[]>('/chat/threads', params as any)).map(toConversation),
  getConversation: async (idStr: string): Promise<PatientConversation> => {
    const r = await http.get<any>(`/chat/threads/${idStr}`);
    const conv = toConversation(r.thread ?? r);
    conv.messages = (r.messages ?? []).map(toChatMessage);
    return conv;
  },
  createConversation: async (data: { conversationType: string; title?: string; participantNhcIds?: string[]; participantNames?: Record<string, string>; linkedProviderId?: string; linkedProviderName?: string }): Promise<PatientConversation> =>
    toConversation(await http.post<any>('/chat/threads', data)),
  sendMessage: async (convId: string, data: { content: string; messageType?: string; metadata?: any; linkedDocumentId?: string; linkedCertificateId?: string; consentGranted?: boolean }): Promise<PatientChatMessage> =>
    toChatMessage(await http.post<any>(`/chat/threads/${convId}/messages`, {
      content: data.content,
      ...(data.messageType && data.messageType !== 'TEXT' ? { messageType: data.messageType } : {}),
      ...(data.metadata || data.linkedDocumentId || data.linkedCertificateId
        ? {
            metadata: {
              ...(data.metadata ?? {}),
              ...(data.linkedDocumentId ? { linkedDocumentId: data.linkedDocumentId } : {}),
              ...(data.linkedCertificateId ? { linkedCertificateId: data.linkedCertificateId } : {}),
              ...(data.consentGranted !== undefined ? { consentGranted: data.consentGranted } : {}),
            },
          }
        : {}),
    })),
  getCalls: (params?: { type?: string; direction?: string; status?: string }) =>
    http.get<PatientCallLogEntry[]>('/communication/calls', params as any),
  getTelemedicine: async (params?: { status?: string }): Promise<PatientTelemedicineSession[]> =>
    (await http.get<any[]>('/telemedicine/consultations', params as any)).map(toTeleSession),

  getGroups: async (): Promise<PatientConversation[]> =>
    (await http.get<any[]>('/chat/groups')).map(toConversation),
  createGroup: async (data: { title: string; conversationType?: string; participantNhcIds?: string[]; participantNames?: Record<string, string> }): Promise<PatientConversation> =>
    toConversation(await http.post<any>('/chat/groups', data)),
  // A chat document-share is a real message with DOCUMENT_SHARE type — the
  // recipient's panel reads the same thread, so nothing extra to invent.
  shareDocumentInChat: async (data: { conversationId: string; certificateId?: string; documentId?: string; consentGranted: boolean }): Promise<PatientChatMessage> =>
    toChatMessage(await http.post<any>(`/chat/threads/${data.conversationId}/messages`, {
      content: 'Shared a document from Health Vault',
      messageType: 'DOCUMENT_SHARE',
      metadata: {
        ...(data.certificateId ? { linkedCertificateId: data.certificateId } : {}),
        ...(data.documentId ? { linkedDocumentId: data.documentId } : {}),
        consentGranted: data.consentGranted,
      },
    })),

  getFavoriteIds: () => http.get<string[]>('/favorites/ids'),
  getFavorites: async (): Promise<MarketplaceProvider[]> => (await http.get<any[]>('/favorites')).map(toProvider),
  addFavorite: (providerId: string) => http.post<any>(`/favorites/${providerId}`),
  removeFavorite: (providerId: string) => http.del<any>(`/favorites/${providerId}`),

  // Lab Integration (rich lab-store flows) — booking/report actions wired to
  // the real backend; discovery extras keep legacy paths (page demo fallback).
  lab: {
    searchLabs: (params?: Record<string, string>) => http.get('/providers', { type: 'LAB', ...(params ?? {}) } as any),
    getLabProfile: (labProfileId: string) => http.get(`/providers/${labProfileId}`),
    getLabTests: (labProfileId: string, params?: Record<string, string>) =>
      http.get('/lab/tests', { labId: labProfileId, ...(params ?? {}) } as any),
    getLabPackages: (labProfileId: string) => http.get('/lab/packages', { labId: labProfileId } as any),
    /**
     * Every published lab's catalogue — the tests a patient can book, whoever
     * runs them, each row carrying its lab and that lab's price. Naming no lab
     * used to be a validation error, so a screen asking "what can I book?"
     * could only ask one lab at a time.
     */
    catalogue: (): Promise<LabCatalogueTest[]> => http.get('/lab/tests'),
    packageCatalogue: (): Promise<LabCataloguePackage[]> => http.get('/lab/packages'),
    /**
     * Every lab's own price for one test, in a single pass over the published
     * catalogues — the server matches the names, so a lab that spells a test
     * differently is not compared against a different test.
     */
    compareTest: (name: string) => http.get<LabTestComparison>('/lab/tests/compare', { name }),
    /** The test names at least two labs publish, so a comparison is worth opening. */
    comparableTests: () => http.get<{ name: string; labCount: number }[]>('/lab/tests/comparable'),
    // Real slot windows the lab configured in its own panel — never seeded.
    getLabSlots: (labProfileId: string, params?: Record<string, string>) =>
      http.get('/lab/slots', { labId: labProfileId, ...(params ?? {}) } as any),
    // Sent as-is: the backend accepts this exact shape (labProfileId/
    // testIds/packageIds/collectionType/slotDate/slotId/referringDoctorName)
    // directly, alongside the simpler bookLab() shape used elsewhere.
    bookAppointment: (data: any) => http.post('/lab/bookings', data),
    getAppointments: () => http.get('/lab/bookings'),
    getAppointment: (idStr: string) => http.get(`/lab/bookings/${idStr}`),
    cancelAppointment: (idStr: string) => http.patch(`/lab/bookings/${idStr}/cancel`),
    /**
     * Home collections the lab has scheduled for this patient, with the
     * phlebotomist, the visit status and its timeline. (The old
     * `/lab/appointments/:id/home-collection/live` path never existed, so the
     * tracking panel silently never appeared.)
     */
    getHomeCollections: (): Promise<LabHomeCollection[]> => http.get('/lab/home-collections'),
    getOrders: () => http.get('/lab/bookings'),
    getOrder: (idStr: string) => http.get(`/lab/bookings/${idStr}`),
    getReports: () => http.get('/lab/reports'),
    getReport: (idStr: string) => http.get(`/lab/reports/${idStr}`),
    getReportInterpretation: async (idStr: string) => {
      const r = await http.get<any>(`/lab/reports/${idStr}`);
      return { interpretation: r.report?.interpretation ?? r.interpretation, file: r.file };
    },
    askNeoAI: (message: string, useRecords?: boolean) => http.post('/neoai/chat', { message, useRecords }),
  },

  /**
   * Invoices & payments — the server's unified list across modules
   * (`GET /payments`), plus one bill's own breakdown. There is no gateway:
   * the detail says how each provider actually takes money.
   */
  /**
   * The pharmacy module on the patient API — richer than the plain order
   * list the app was using: each order carries its timeline, invoice,
   * delivery and payment state, and a pharmacy's proposed substitution can
   * finally be accepted or declined.
   */
  pharmacyModule: {
    orders: () => http.get<PharmacyOrderFull[]>('/pharmacy/orders'),
    /** What doctors have prescribed, ready to order. */
    prescribedMedicines: () => http.get<PrescribedMedicine[]>('/pharmacy/prescribed-medicines'),

    /** Substitutes a pharmacy proposed and is waiting on an answer for. */
    substitutions: () => http.get<PharmacySubstitution[]>('/pharmacy/substitutions'),
    /**
     * The decision goes back where the pharmacist is looking: onto the
     * proposal, and into the order's chat as the patient's own message.
     */
    decideSubstitution: (idStr: string, decision: 'APPROVE' | 'DECLINE') =>
      http.post<unknown>(`/pharmacy/substitutions/${idStr}/decision`, { decision }),

    /**
     * Which pharmacies can actually fill one prescription, and roughly for
     * how much. The server matches each prescribed medicine against every
     * public catalogue by name and then generic, and reports what it could
     * not find rather than quietly costing a shorter list.
     */
    quote: (prescriptionId: string) =>
      http.get<PrescriptionQuote>('/pharmacy/quotes', { prescriptionId }),
  },

  /**
   * Ambulance — the trips this patient booked and what a crew would be shown.
   *
   * The web app could only fire an SOS: a booked trip had no list, no detail,
   * no way to cancel, and the emergency profile a crew reads was invisible to
   * the patient it describes.
   */
  ambulance: {
    trips: () => http.get<AmbulanceTrip[]>('/ambulance/requests'),
    /** One request, for a screen that has to watch a fleet accept it. */
    trip: (idStr: string) => http.get<AmbulanceTrip>(`/ambulance/requests/${idStr}`),
    cancel: (idStr: string, reason?: string) =>
      http.patch<AmbulanceTrip>(`/ambulance/requests/${idStr}/cancel`, reason ? { reason } : undefined),
    /**
     * What the published fare classes start at. Empty when no fleet is
     * quoting, which the screen says rather than inventing a price.
     */
    fareEstimate: (params?: { ambulanceType?: string; distanceKm?: number; requestType?: string }) =>
      http.get<AmbulanceFareEstimate>('/ambulance/fare-estimate', params as Record<string, string | number>),

    /** Book one. The app could only fire an SOS; this is the real request. */
    book: (data: AmbulanceBookingInput) => http.post<AmbulanceTrip>('/ambulance/requests', data),

    /** What the crew sees on arrival — blood group, allergies, contacts. */
    emergencyProfile: () => http.get<AmbulanceEmergencyProfile>('/ambulance/emergency-profile'),
    destinations: () => http.get<AmbulanceDestination[]>('/ambulance/destinations'),
  },

  /**
   * Care plans — a goal the patient is working towards and the routine that
   * goes with it (`GET/POST /care-plans`, `DELETE /care-plans/:groupId`).
   * Nothing on the web read or wrote them, so plans made elsewhere were
   * invisible here. This account has two.
   */
  carePlans: {
    list: () => http.get<CarePlan[]>('/care-plans'),
    create: (plan: {
      groupId: string;
      goalLabel: string;
      goalIcon?: string;
      planKey?: string;
      summary?: string;
      endDate?: string;
    }) => http.post<CarePlan>('/care-plans', plan),
    /**
     * Stops the plan AND ends every reminder it created — the server reports
     * how many in `itemsEnded`, which the page tells the patient rather than
     * letting a plan's whole routine disappear unannounced.
     */
    remove: (groupId: string) =>
      http.del<{ removed: boolean; itemsEnded: number }>(`/care-plans/${encodeURIComponent(groupId)}`),
  },

  /**
   * Who this patient is connected to across the network — `GET /ecosystem`.
   * The ecosystem page was static; this is the real count per provider type.
   */
  ecosystem: () => http.get<EcosystemOverview>('/ecosystem'),

  /** Every SOS this patient has raised, and what happened to it. */
  emergencyRequests: () => http.get<EmergencyRequestRow[]>('/emergency/requests'),

  /**
   * The preferences blob on the profile — read, merged and written back
   * through `PATCH /profile`.
   *
   * There is no dedicated endpoint for a delivery address book or care
   * defaults; the profile's `preferences` object is the store, and the
   * server accepts it as an open record. Merging client-side is what keeps
   * one screen's save from wiping another's keys.
   */
  getPreferences: async (): Promise<PatientPreferences> => {
    const p = await http.get<{ preferences?: PatientPreferences }>('/profile');
    return p?.preferences ?? {};
  },

  savePreferences: async (patch: PatientPreferences): Promise<PatientPreferences> => {
    const current = await patientApi.getPreferences();
    const merged = { ...current, ...patch };
    const saved = await http.patch<{ preferences?: PatientPreferences }>('/profile', { preferences: merged });
    return saved?.preferences ?? merged;
  },

  /**
   * The Health Vault — `GET /vault`.
   *
   * This is the WHOLE vault: prescriptions, lab and imaging reports,
   * certificates, vaccinations, insurance documents, pharmacy invoices,
   * ambulance trip reports and the patient's own uploads, each row carrying
   * its real issuer, document number and what the registry says about it.
   *
   * `/health-vault/documents` is a narrower thing — ONLY the patient's own
   * uploads, with no issuer on any row. The Health Vault page used to read
   * that and pad the rest out of the device's own store, so a page promising
   * every record showed two of eighteen and marked genuinely verified
   * documents "pending".
   */
  vault: (type?: string) =>
    http.get<VaultRecord[]>('/vault', type && type !== 'ALL' ? { type } : undefined),

  /**
   * The record rendered to a PDF the patient can hand to someone —
   * `GET /neoverify/:id/pdf`.
   *
   * Most of the vault is rows in a database, not a file: a prescription has
   * nothing to "download" until it is rendered. Only a registered record has
   * a NeoVerify id, and only a NeoVerify id has anything to render.
   */
  neoVerifyPdf: (neoVerifyId: string): Promise<Blob> =>
    http.blob(`/neoverify/${encodeURIComponent(neoVerifyId.trim())}/pdf`),

  /**
   * Record that the patient opened or shared a registered document —
   * `POST /neoverify/:id/access`.
   *
   * The document's own history has labels ready for these — "Opened by you",
   * "Shared by you" — and neither client ever called this, so those lines could
   * never appear. A download is logged by the PDF route itself.
   *
   * Best-effort: a patient opening their own document must not be blocked
   * because the audit write failed.
   */
  logDocumentAccess: (neoVerifyId: string, action: 'VIEW' | 'DOWNLOAD' | 'SHARE') =>
    http.post(`/neoverify/${encodeURIComponent(neoVerifyId.trim())}/access`, { action })
      .catch(() => { /* the history is one line poorer; the action still happened */ }),

  /**
   * One document's own history — `GET /neoverify/:id/provenance`.
   *
   * Issued, registered, and every check, download and share since. Not the
   * account-wide access log: this is what happened to THIS document.
   */
  neoVerifyProvenance: (neoVerifyId: string) =>
    http.get<VaultProvenance>(`/neoverify/${encodeURIComponent(neoVerifyId.trim())}/provenance`),

  /**
   * Check one document id against the registry — `GET /neoverify/:id`.
   *
   * The verdict is the registry's and is quoted as given. An id that was
   * printed but never registered comes back UNREGISTERED, which on paper
   * looks identical to a genuine one — so it is shown as plainly as a pass.
   */
  neoVerify: (neoVerifyId: string) =>
    http.get<NeoVerifyResult>(`/neoverify/${encodeURIComponent(neoVerifyId.trim())}`),

  /**
   * Requests this patient has filed, and what came back — `GET/POST
   * /support/requests` and `POST /support/requests/:id/replies`.
   *
   * Nothing on the web called any of the three. A patient who raised a
   * records question, a billing query or a data-protection request could
   * never see whether anyone had answered; this account has a reply from
   * NeoHealthCard Care that has never been shown.
   *
   * There is no separate rights endpoint: a data-protection request is filed
   * here under the PRIVACY topic, where the officer actions it and the trail
   * is auditable.
   */
  supportRequests: {
    list: () => http.get<SupportRequest[]>('/support/requests'),
    create: (data: { topic: SupportTopic; subject: string; message: string; contact?: string }) =>
      http.post<SupportRequest>('/support/requests', data),
    reply: (idStr: string, message: string) =>
      http.post<SupportRequest>(`/support/requests/${idStr}/replies`, { message: message.trim() }),
  },

  /**
   * Findings a provider flagged as needing the patient's attention now —
   * `GET /home/alerts`. This patient has two CRITICAL lab findings on file
   * that no web screen has ever shown, because nothing called this.
   */
  homeAlerts: () => http.get<HomeAlert[]>('/home/alerts'),

  /**
   * The patient's hospital record — encounters, today's live queue tokens and
   * the bills the hospital chose to share. Four endpoints the web app never
   * called, so a patient admitted to a ward with an unpaid bill saw nothing.
   */
  hospital: {
    summary: () => http.get<HospitalSummary>('/hospital/summary'),
    encounters: () => http.get<HospitalEncounter[]>('/hospital/encounters'),
    /** Same shape as an encounter — only today's, and only while live. */
    queue: () => http.get<HospitalEncounter[]>('/hospital/queue'),
    bills: () => http.get<HospitalBill[]>('/hospital/bills'),

    /**
     * The hospitals that have switched their own public panel on, with what
     * each of them chose to publish — departments, doctors, a price list,
     * licences and whether it takes bookings through the app.
     *
     * This is a different record from the marketplace hospital card: it is
     * the hospital's own storefront, maintained in the hospital's app, and it
     * is the only one that can be booked into. A hospital that has not turned
     * its panel on is simply not here.
     */
    directory: (params?: { city?: string; q?: string; page?: number; limit?: number }) =>
      http.get<HospitalDirectory>('/hospitals', params as Record<string, string | number>),
    panel: (idStr: string) => http.get<HospitalPanel>(`/hospitals/${idStr}`),
    /**
     * Book OPD at a hospital that opened public booking. The fee is read from
     * the hospital's own published list and the patient's identity from the
     * account — neither is accepted from here, so neither is sent.
     */
    book: (idStr: string, data: HospitalBookingInput) =>
      http.post<HospitalBookingResult>(`/hospitals/${idStr}/appointments`, data),
  },

  payments: {
    list: (): Promise<PatientPaymentsList> => http.get('/payments'),
    detail: (module: string, reference: string): Promise<PatientPaymentDetail> =>
      http.get(`/payments/${encodeURIComponent(module)}/${encodeURIComponent(reference)}`),

    /**
     * The receipt for a payment the provider has recorded. An unpaid bill has
     * none, and the server says so rather than rendering a document for a
     * payment nobody confirmed.
     */
    receipt: (module: string, reference: string): Promise<PaymentReceipt> =>
      http.get(`/payments/${encodeURIComponent(module)}/${encodeURIComponent(reference)}/receipt`),

    /**
     * Tell the provider a direct transfer was made. This app takes no money:
     * the reference is passed to the provider to confirm, and the bill stays
     * unpaid until they do.
     */
    submitProof: (
      module: string,
      reference: string,
      data: { method: 'DIRECT_UPI' | 'BANK_TRANSFER'; utr: string; note?: string },
    ): Promise<unknown> =>
      http.post(`/payments/${encodeURIComponent(module)}/${encodeURIComponent(reference)}/proof`, {
        method: data.method,
        utr: data.utr.trim(),
        ...(data.note?.trim() ? { note: data.note.trim() } : {}),
      }),
  },

};
